/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiResponse } from '../../types/apiTypes';
import { Agreement } from '../../types/agreementTypes';
import { Location } from '../../types/locationTypes';
import {
  CreateVisitorWorkflowPayload,
  UpdateVisitorWorkflowConfig,
  UpdateVisitorWorkflowFields,
  UpdateVisitorWorkflowPayload,
  VisitorWorkflow,
  VisitorWorkflowConfig,
  VisitorWorkflowFields,
  VisitorWorkflowType,
} from '../../types/visitorWorkflowTypes';

export interface VisitorWorkflowsState {
  visitorWorkflows: ApiResponse<VisitorWorkflow>;
  selectedVisitorWorkflow: VisitorWorkflow | null;
  selectedVisitorWorkflowLocations: ApiResponse<Location>;
  selectedSignInWorkflowConfig: VisitorWorkflowConfig | null;
  selectedSignInWorkflowFields: VisitorWorkflowFields | null;
  globalSignInWorkflowConfig: VisitorWorkflowConfig | null;
  globalSignInWorkflowFields: VisitorWorkflowFields | null;
  visitorAgreements: ApiResponse<Agreement>;
  workflowTypes: VisitorWorkflowType[];
  fetchWorkflowTypesLoading: boolean;
  fetchVisitorWorkflowsLoading: boolean;
  describeVisitorWorkflowLoading: boolean;
  createVisitorWorkflowLoading: boolean;
  updateVisitorWorkflowLoading: boolean;
  deleteVisitorWorkflowLoading: boolean;
  describeVisitorWorkflowLocationsLoading: boolean;
  describeVisitorWorkflowConfigLoading: boolean;
  updateVisitorWorkflowConfigLoading: boolean;
  describeVisitorWorkflowFieldsLoading: boolean;
  updateVisitorWorkflowFieldsLoading: boolean;
  fetchVisitorAgreementsLoading: boolean;
  bulkUpdateVisitorAgreementsLoading: boolean;
  error: string | null;
}

const initialState: VisitorWorkflowsState = {
  visitorWorkflows: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  selectedVisitorWorkflow: null,
  selectedVisitorWorkflowLocations: {
    data: [],
    meta: null,
    totalCount: 0,
    filteredCount: 0,
  },
  selectedSignInWorkflowConfig: null,
  selectedSignInWorkflowFields: null,
  globalSignInWorkflowConfig: null,
  globalSignInWorkflowFields: null,
  visitorAgreements: { data: [], meta: null, totalCount: 0, filteredCount: 0 },

  workflowTypes: [],
  fetchWorkflowTypesLoading: false,

  fetchVisitorWorkflowsLoading: false,
  describeVisitorWorkflowLoading: false,
  createVisitorWorkflowLoading: false,
  updateVisitorWorkflowLoading: false,
  deleteVisitorWorkflowLoading: false,
  describeVisitorWorkflowLocationsLoading: false,
  describeVisitorWorkflowConfigLoading: false,
  updateVisitorWorkflowConfigLoading: false,
  describeVisitorWorkflowFieldsLoading: false,
  updateVisitorWorkflowFieldsLoading: false,
  fetchVisitorAgreementsLoading: false,
  bulkUpdateVisitorAgreementsLoading: false,
  error: null,
};

type VisitorWorkflowsLoadingKey =
  | 'fetchWorkflowTypesLoading'
  | 'fetchVisitorWorkflowsLoading'
  | 'describeVisitorWorkflowLoading'
  | 'createVisitorWorkflowLoading'
  | 'updateVisitorWorkflowLoading'
  | 'deleteVisitorWorkflowLoading'
  | 'describeVisitorWorkflowLocationsLoading'
  | 'describeVisitorWorkflowConfigLoading'
  | 'updateVisitorWorkflowConfigLoading'
  | 'describeVisitorWorkflowFieldsLoading'
  | 'updateVisitorWorkflowFieldsLoading'
  | 'fetchVisitorAgreementsLoading'
  | 'bulkUpdateVisitorAgreementsLoading';

const visitorWorkflowsSlice = createSlice({
  name: 'visitorWorkflows',
  initialState,
  reducers: {
    fetchVisitorWorkflowTypes: (
      state,
      _action: PayloadAction<{ orgId: number }>,
    ) => {
      state.fetchWorkflowTypesLoading = true;
    },
    setVisitorWorkflowTypes: (
      state,
      action: PayloadAction<ApiResponse<VisitorWorkflowType>>,
    ) => {
      state.workflowTypes = action.payload.data;
      state.fetchWorkflowTypesLoading = false;
    },

    fetchVisitorWorkflows: (
      _state,
      _action: PayloadAction<{ orgId: number; workflowTypeId?: number }>,
    ) => {},
    describeVisitorWorkflow: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitorWorkflowId: number;
        workflowTypeId?: number;
      }>,
    ) => {},
    createVisitorWorkflow: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitorWorkflow: CreateVisitorWorkflowPayload;
      }>,
    ) => {},
    updateVisitorWorkflow: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitorWorkflowId: number;
        visitorWorkflow: UpdateVisitorWorkflowPayload;
      }>,
    ) => {},
    deleteVisitorWorkflow: (
      _state,
      _action: PayloadAction<{ orgId: number; visitorWorkflowId: number }>,
    ) => {},
    describeVisitorWorkflowLocations: (
      _state,
      _action: PayloadAction<{ orgId: number; visitorWorkflowId: number }>,
    ) => {},
    describeVisitorWorkflowConfig: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitorWorkflowId: number;
        global?: boolean;
      }>,
    ) => {},
    updateVisitorWorkflowConfig: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitorWorkflowId: number;
        visitorWorkflowConfigParams: UpdateVisitorWorkflowConfig;
      }>,
    ) => {},
    describeVisitorWorkflowFields: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitorWorkflowId: number;
        global?: boolean;
      }>,
    ) => {},
    updateVisitorWorkflowFields: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitorWorkflowId: number;
        visitorWorkflowFields: UpdateVisitorWorkflowFields;
      }>,
    ) => {},
    fetchVisitorWorkflowAgreements: (
      _state,
      _action: PayloadAction<{ orgId: number; visitorWorkflowId: number }>,
    ) => {},
    bulkUpdateVisitorWorkflowAgreements: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitorWorkflowId: number;
        add: number[];
        remove: number[];
      }>,
    ) => {},

    setVisitorWorkflows: (
      state,
      action: PayloadAction<ApiResponse<VisitorWorkflow>>,
    ) => {
      state.visitorWorkflows = action.payload;
    },
    setSelectedVisitorWorkflow: (
      state,
      action: PayloadAction<ApiResponse<VisitorWorkflow>>,
    ) => {
      state.selectedVisitorWorkflow = action.payload.data[0] || null;
    },
    setVisitorWorkflowLocations: (
      state,
      action: PayloadAction<ApiResponse<Location>>,
    ) => {
      state.selectedVisitorWorkflowLocations = action.payload;
    },
    setVisitorWorkflowConfig: (
      state,
      action: PayloadAction<
        ApiResponse<VisitorWorkflowConfig> & { global?: boolean }
      >,
    ) => {
      if (action.payload.global) {
        state.globalSignInWorkflowConfig = action.payload.data[0];
      } else state.selectedSignInWorkflowConfig = action.payload.data[0];
    },
    setVisitorWorkflowFields: (
      state,
      action: PayloadAction<
        ApiResponse<VisitorWorkflowFields> & { global?: boolean }
      >,
    ) => {
      if (action.payload.global) {
        state.globalSignInWorkflowFields = action.payload.data[0];
      } else state.selectedSignInWorkflowFields = action.payload.data[0];
    },
    setVisitorAgreements: (
      state,
      action: PayloadAction<ApiResponse<Agreement>>,
    ) => {
      state.visitorAgreements = action.payload;
    },
    setLoading: (
      state,
      action: PayloadAction<{
        key: VisitorWorkflowsLoadingKey;
        value: boolean;
      }>,
    ) => {
      state[action.payload.key] = action.payload.value;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearVisitorWorkflows: (state) => {
      state.visitorWorkflows = initialState.visitorWorkflows;
    },
    clearSelectedVisitorWorkflow: (state) => {
      state.selectedVisitorWorkflow = null;
    },
    clearVisitorWorkflowLocations: (state) => {
      state.selectedVisitorWorkflowLocations =
        initialState.selectedVisitorWorkflowLocations;
    },
    clearVisitorWorkflowConfig: (state) => {
      state.selectedSignInWorkflowConfig = null;
    },
    clearVisitorWorkflowsState: () => initialState,
    clearGlobalSignInWorkflow: (state) => {
      state.globalSignInWorkflowConfig = null;
      state.globalSignInWorkflowFields = null;
    },
  },
});

export const visitorWorkflowsActions = visitorWorkflowsSlice.actions;

export const {
  fetchVisitorWorkflowTypes,
  setVisitorWorkflowTypes,
  fetchVisitorWorkflows,
  describeVisitorWorkflow,
  createVisitorWorkflow,
  updateVisitorWorkflow,
  deleteVisitorWorkflow,
  describeVisitorWorkflowLocations,
  describeVisitorWorkflowConfig,
  updateVisitorWorkflowConfig,
  describeVisitorWorkflowFields,
  updateVisitorWorkflowFields,
  fetchVisitorWorkflowAgreements,
  bulkUpdateVisitorWorkflowAgreements,
  setVisitorWorkflows,
  setSelectedVisitorWorkflow,
  setVisitorWorkflowLocations,
  setVisitorWorkflowConfig,
  setVisitorWorkflowFields,
  setVisitorAgreements,
  setLoading,
  setError,
  clearVisitorWorkflows,
  clearSelectedVisitorWorkflow,
  clearVisitorWorkflowLocations,
  clearVisitorWorkflowConfig,
  clearVisitorWorkflowsState,
  clearGlobalSignInWorkflow,
} = visitorWorkflowsSlice.actions;

export default visitorWorkflowsSlice.reducer;
